import { RefObject, useEffect, useState } from 'react'

/** Piso: por debajo de esto la grilla deja de ser usable y conviene que la página scrollee. */
const MIN_HEIGHT = 300

/**
 * Espacio ocupado por debajo del elemento hasta la raíz del documento.
 *
 * Recorre la cadena de ancestros sumando, en cada nivel, lo que hay debajo del
 * nodo dentro de su padre: `paddingBottom` y `borderBottomWidth` del padre, más
 * el `marginBottom` del propio nodo. No hay doble conteo porque cada paso mide
 * dentro de su propia caja.
 *
 * No se usa `body.getBoundingClientRect().bottom` a propósito: la shell estira
 * el body con un alto mínimo, así que su borde inferior no es el fondo del
 * contenido y ese holgura se colaría en la cuenta, dejando la grilla más chica
 * de lo necesario y un hueco vacío abajo.
 */
function measureSpaceBelow(el: HTMLElement): number {
  let total = 0
  let node: HTMLElement | null = el

  while (node && node !== document.body) {
    // Anotado a mano: `node` se reasigna con este valor y sin el tipo explícito
    // la inferencia se vuelve circular.
    const parent: HTMLElement | null = node.parentElement
    if (!parent) break

    const parentStyle = getComputedStyle(parent)

    total += parseFloat(getComputedStyle(node).marginBottom) || 0
    total += parseFloat(parentStyle.paddingBottom) || 0
    total += parseFloat(parentStyle.borderBottomWidth) || 0

    node = parent
  }

  return total
}

/**
 * Alto que puede ocupar el elemento para llenar el viewport sin desbordarlo.
 *
 * Reemplaza a un `calc(100vh - Npx)` con N estimado a mano. Esa estimación es
 * frágil porque el chrome de arriba cambia sin que cambie el viewport: se
 * agregan chips de filtro, la leyenda envuelve, aparece la línea de marca
 * resaltada. Midiendo la posición real el alto cierra exacto en ambos sentidos:
 * ni sobra espacio abajo ni aparece scroll de ventana.
 */
export function useAvailableHeight(ref: RefObject<HTMLElement>): number | null {
  const [height, setHeight] = useState<number | null>(null)

  useEffect(() => {
    const measure = () => {
      const el = ref.current
      if (!el) return

      const top = el.getBoundingClientRect().top
      const available = Math.max(
        MIN_HEIGHT,
        Math.round(window.innerHeight - top - measureSpaceBelow(el))
      )

      // Sólo ante un cambio real: fijar el alto modifica el alto del body, que
      // es justo lo que observamos. Sin este corte el observer se retroalimenta
      // y oscila por diferencias de subpíxel.
      setHeight((prev) => (prev !== null && Math.abs(prev - available) < 2 ? prev : available))
    }

    measure()
    window.addEventListener('resize', measure)

    // El chrome cambia sin que cambie el viewport, así que `resize` no alcanza.
    const observer = new ResizeObserver(measure)
    observer.observe(document.body)

    return () => {
      window.removeEventListener('resize', measure)
      observer.disconnect()
    }
  }, [ref])

  return height
}
